import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { isFeatureEnabled } from '../../../services/featureEnabledService';
import { MID_SHIFT_REPLAN_FEATURE_NAME } from '../../../utils/midShiftActuals';

/**
 * Feature flag for the mid-shift re-plan actual-vs-projected display (UTR-REFLEX-1612/1613).
 * Gated via the RnRRCCFacadeService IsFeatureEnabled / AppConfig mechanism: enabled when the user
 * (FeatureGating.RCC_MID_SHIFT_REPLAN.AllowedUsers) OR the site (.AllowedSites) is allowlisted, as
 * configured in RnRRCCShiftPlanningCommons FeatureFlags.cfg.
 *
 * Shared via Redux (same pattern as publishedShiftPlanFeatureSlice) because several surfaces read
 * it: the published-plan and review pages, the volume chart and the connected Process Path table.
 */

// ─── State Interface ────────────────────────────────────────────────────────────

export interface MidShiftReplanFeatureState {
  /**
   * Whether actual (elapsed) hours are shown alongside projections. `null` = not yet resolved;
   * consumers treat anything but `true` as off (fail closed), which hides actual hours.
   */
  isMidShiftReplanEnabled: boolean | null;
  /**
   * Station of the most recent check ('' = station-less). A station-less check and a real-station
   * check can be in flight together; only the latest one's result is applied, so a slower
   * station-less response can't overwrite the real site's answer.
   */
  latestRequestedStation: string | null;
}

// ─── Initial State ──────────────────────────────────────────────────────────────

const initialState: MidShiftReplanFeatureState = {
  isMidShiftReplanEnabled: null,
  latestRequestedStation: null,
};

// ─── Async Thunks ───────────────────────────────────────────────────────────────

export const checkMidShiftReplanEnabled = createAsyncThunk<
  boolean,
  string,
  { rejectValue: string }
>(
  'midShiftReplan/checkFeatureEnabled',
  async (stationCode, { rejectWithValue }) => {
    try {
      // Pass the station so the facade's site-level gating (AllowedSites) applies, not just users.
      return await isFeatureEnabled(MID_SHIFT_REPLAN_FEATURE_NAME, stationCode);
    } catch (error: unknown) {
      return rejectWithValue('Failed to check feature flag');
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────────────────────────

const midShiftReplanFeatureSlice = createSlice({
  name: 'midShiftReplanFeature',
  initialState,
  reducers: {
    /**
     * Directly set the flag without hitting the facade. Used in local dev where getUserName() (and
     * therefore isFeatureEnabled) can't resolve without a Boson parent frame.
     */
    setMidShiftReplanEnabled(state, action: PayloadAction<boolean>) {
      state.isMidShiftReplanEnabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkMidShiftReplanEnabled.pending, (state, action) => {
        state.latestRequestedStation = action.meta.arg;
      })
      .addCase(checkMidShiftReplanEnabled.fulfilled, (state, action) => {
        if (action.meta.arg !== state.latestRequestedStation) {
          return; // superseded by a later check
        }
        state.isMidShiftReplanEnabled = action.payload;
      })
      .addCase(checkMidShiftReplanEnabled.rejected, (state, action) => {
        if (action.meta.arg !== state.latestRequestedStation) {
          return; // superseded by a later check
        }
        // Fail closed — actual hours stay hidden.
        state.isMidShiftReplanEnabled = false;
      });
  },
});

export const { setMidShiftReplanEnabled } = midShiftReplanFeatureSlice.actions;

/** True only once the flag has resolved to enabled. */
export const selectIsMidShiftReplanEnabled = (state: {
  midShiftReplanFeature?: Pick<MidShiftReplanFeatureState, 'isMidShiftReplanEnabled'>;
}): boolean => state.midShiftReplanFeature?.isMidShiftReplanEnabled === true;

export default midShiftReplanFeatureSlice.reducer;
